import { PROVIDER_LEVELS, US_STATES } from '@ems/clinical';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Building2, Download, LogOut, Monitor, Moon, Plus, Sun, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Badge, Button, ButtonLink, Card, CardHeader, Field, Input, PageHeader, Segmented, Select, Switch } from '@/components/ui';
import { workerFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { APP_NAME, isBackendConfigured } from '@/lib/env';
import { currentPushSubscription, disablePush, enablePush, pushSupported } from '@/lib/push';
import { clearOfflineData } from '@/lib/queryClient';
import { db, unwrap } from '@/lib/db';
import { errorMessage, ORG_TYPE_LABELS, ROLE_LABELS } from '@/lib/utils';
import { applyTheme, usePrefs, type Theme } from '@/stores/prefs';

type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
let deferredInstall: InstallPromptEvent | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstall = e as InstallPromptEvent;
  });
}

function ProfileForm() {
  const { profile, refresh } = useAuth();
  const [form, setForm] = useState({ full_name: '', certification_level: '', state_code: '', license_number: '' });
  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name ?? '',
        certification_level: profile.certification_level ?? '',
        state_code: profile.state_code ?? '',
        license_number: profile.license_number ?? '',
      });
    }
  }, [profile]);
  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await db
          .from('profiles')
          .update({
            full_name: form.full_name.trim(),
            certification_level: form.certification_level || null,
            state_code: form.state_code || null,
            license_number: form.license_number.trim() || null,
          })
          .eq('id', profile!.id),
      ),
    onSuccess: refresh,
  });

  return (
    <Card>
      <CardHeader title="Your profile" subtitle={profile?.email} />
      <form
        className="grid gap-3 p-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Full name">
          <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
        </Field>
        <Field label="Certification level">
          <Select value={form.certification_level} onChange={(e) => setForm({ ...form, certification_level: e.target.value })}>
            <option value="">Not specified</option>
            {PROVIDER_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Licensing state">
          <Select value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value })}>
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="License / certification number">
          <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
        </Field>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Button type="submit" loading={save.isPending}>
            Save profile
          </Button>
          {save.isSuccess && <span className="text-sm text-emerald-600">Saved</span>}
          {save.isError && <span className="text-sm text-red-600">{errorMessage(save.error)}</span>}
        </div>
      </form>
    </Card>
  );
}

function Memberships() {
  const { memberships, activeOrgId, setActiveOrgId, refresh, user } = useAuth();
  const queryClient = useQueryClient();
  const leave = useMutation({
    mutationFn: async (orgId: string) => unwrap(await db.rpc('remove_org_member', { p_org: orgId, p_user: user!.id })),
    onSuccess: async () => {
      await refresh();
      queryClient.invalidateQueries();
    },
  });
  return (
    <Card>
      <CardHeader
        title="Organizations"
        subtitle="Each organization provides its own protocols, formulary, and education."
        action={
          <ButtonLink size="sm" variant="outline" to="/orgs/new" icon={<Plus className="size-4" />}>
            Create
          </ButtonLink>
        }
      />
      <div className="p-4">
        {memberships.length === 0 ? (
          <p className="text-sm text-stone-500">You’re not a member of any organization. Ask your administrator for an invite link, or create an organization.</p>
        ) : (
          <ul className="space-y-2">
            {memberships.map((m) => (
              <li key={m.org_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 p-3 dark:border-stone-700">
                <Building2 className="size-5 text-stone-400" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{m.organization.name}</div>
                  <div className="text-sm text-stone-500">
                    {ORG_TYPE_LABELS[m.organization.type]} · {ROLE_LABELS[m.role]}
                  </div>
                </div>
                {m.org_id === activeOrgId ? (
                  <Badge tone="success">Active</Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setActiveOrgId(m.org_id)}>
                    Switch
                  </Button>
                )}
                {m.role !== 'owner' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => confirm(`Leave ${m.organization.name}?`) && leave.mutate(m.org_id)}
                    loading={leave.isPending && leave.variables === m.org_id}
                  >
                    Leave
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {leave.isError && <Alert tone="danger" className="mt-2">{errorMessage(leave.error)}</Alert>}
      </div>
    </Card>
  );
}

function Notifications() {
  const { user } = useAuth();
  const subscription = useQuery({ queryKey: ['push-subscription'], queryFn: currentPushSubscription, gcTime: 0 });
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: async (enable: boolean) => (enable ? enablePush(user!.id) : disablePush()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['push-subscription'] }),
  });
  const test = useMutation({ mutationFn: () => workerFetch<{ delivered: number }>('/push/test', { method: 'POST' }) });
  const enabled = Boolean(subscription.data);

  return (
    <Card>
      <CardHeader title="Notifications" icon={<BellRing className="size-5" />} />
      <div className="space-y-3 p-4">
        {!pushSupported() ? (
          <Alert tone="info">Push notifications aren’t available in this browser. On iPhone and iPad, add {APP_NAME} to your Home Screen first.</Alert>
        ) : (
          <Switch
            checked={enabled}
            disabled={toggle.isPending || !user}
            onChange={(v) => toggle.mutate(v)}
            label="Push notifications on this device"
            description="Protocol updates, announcements, and assignment reminders"
          />
        )}
        {enabled && (
          <Button size="sm" variant="outline" loading={test.isPending} onClick={() => test.mutate()}>
            Send test notification
          </Button>
        )}
        {test.isSuccess && <p className="text-sm text-emerald-600">Sent to {test.data.delivered} device(s).</p>}
        {(toggle.isError || test.isError) && <Alert tone="danger">{errorMessage(toggle.error ?? test.error)}</Alert>}
      </div>
    </Card>
  );
}

function AppSettings() {
  const { theme, setTheme } = usePrefs();
  const [cleared, setCleared] = useState(false);
  const [canInstall, setCanInstall] = useState(Boolean(deferredInstall));
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  useEffect(() => {
    const onPrompt = () => setCanInstall(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  return (
    <Card>
      <CardHeader title="App" />
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium">Theme</span>
          <Segmented<Theme>
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'system', label: <Monitor className="size-4" aria-label="System" /> },
              { value: 'light', label: <Sun className="size-4" aria-label="Light" /> },
              { value: 'dark', label: <Moon className="size-4" aria-label="Dark" /> },
            ]}
          />
        </div>
        {canInstall && (
          <Button
            variant="outline"
            icon={<Download className="size-4" />}
            onClick={async () => {
              await deferredInstall?.prompt();
              deferredInstall = null;
              setCanInstall(false);
            }}
          >
            Install app on this device
          </Button>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-medium">Offline data</div>
            <div className="text-sm text-stone-500">Saved protocols, formulary, and attachments</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<Trash2 className="size-4" />}
            onClick={async () => {
              await clearOfflineData();
              setCleared(true);
            }}
          >
            Clear
          </Button>
        </div>
        {cleared && <p className="text-sm text-emerald-600">Offline data cleared. It will re-download when online.</p>}
      </div>
    </Card>
  );
}

export function ProfilePage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <PageHeader title="Profile & settings" />
      {isBackendConfigured && user ? (
        <>
          <ProfileForm />
          <Memberships />
          <Notifications />
        </>
      ) : (
        isBackendConfigured && (
          <Alert tone="info" title="Not signed in" action={<ButtonLink size="sm" to="/login">Sign in</ButtonLink>}>
            Sign in to access your agency’s protocols and education.
          </Alert>
        )
      )}
      <AppSettings />
      {user && (
        <Button
          variant="outline"
          className="w-full"
          icon={<LogOut className="size-4" />}
          onClick={async () => {
            await signOut();
            navigate('/login');
          }}
        >
          Sign out
        </Button>
      )}
      <p className="text-center text-xs text-stone-500">
        {APP_NAME} provides clinical decision support. It does not replace local protocols, medical control, or clinical judgment.
      </p>
    </div>
  );
}
